import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MessageSquarePlus, RotateCcw } from "lucide-react";

import {
  archiveOpportunityResearchChat,
  createOpportunityResearchChat,
  findLatestOpportunityResearchChat,
  getOpportunityResearchChat,
  hasBotReplyTo,
  postOpportunityResearchMessage,
  OpportunityResearchUnavailableError,
  OPPORTUNITY_RESEARCH_POLL_INTERVAL_MS,
  OPPORTUNITY_RESEARCH_POLL_MAX_ATTEMPTS,
} from "@/api/opportunity_research_chats";
import {
  listGrantOpportunityDocuments,
  type GrantOpportunityDocumentLink,
} from "@/api/grant_opportunity_documents";
import { OrdinalIcon } from "@/components/OrdinalIcon";
import Citations from "@/components/chat/Citations";
import {
  buildMarkdownComponents,
  citationUrlTransform,
} from "@/components/chat/citationMarkdown";
import {
  buildCitationDisplayNumbers,
  citationEntryKey,
  detectCitationMarkerStyle,
  normalizeCitationLinks,
  type CitationEntry,
} from "@/components/chat/citationUtils";
import {
  ChatAssistantBubble,
  ChatComposer,
  ChatInlineAlert,
  ChatLoadingIndicator,
  ChatMessageThread,
  ChatUserBubble,
  ChatWelcomeBubble,
  type ChatDisplayMessage,
} from "@/components/chat/primitives";
import { Button } from "@/components/ui/button";
import { sanitizeLLMOutput } from "@/lib/sanitizeLlmOutput";
import type { ChatConversation, ChatMessage, CitationSource } from "@/types/chat";

const OPPORTUNITY_RESEARCH_WELCOME =
  "Ask about this opportunity's grant package and confirmed plan. Answers use only this opportunity's materials—not your document library or the web.";

type OpportunityDisplayMessage = ChatDisplayMessage & {
  citations?: CitationEntry[];
  citationSources?: CitationSource[];
};

function displayMessageFromChat(message: ChatMessage): OpportunityDisplayMessage {
  return {
    id: message.id,
    role: message.source === "from_human" ? "user" : "assistant",
    content: message.content,
    createdAt: message.created_at,
    citations: message.meta_data?.citation,
    citationSources: message.meta_data?.citation_sources,
  };
}

function visibleMessages(chat: ChatConversation | null): OpportunityDisplayMessage[] {
  return (chat?.messages ?? [])
    .map(displayMessageFromChat)
    .filter((message) => message.content.trim().length > 0);
}

/**
 * citation_key => package document URL for the References rows. Resolved
 * against the live document links (not persisted URLs) so a source removed
 * from the package degrades to plain text instead of a dead link.
 *
 * A key that maps to more than one distinct package document (two files with
 * the same display name and no heading collapse to the same key) gets no
 * link: plain text beats confidently opening the wrong file.
 */
function sourceHrefsForMessage(
  sources: CitationSource[] | undefined,
  links: GrantOpportunityDocumentLink[],
): Map<string, string> | undefined {
  if (!sources?.length || !links.length) return undefined;

  const documentsByKey = new Map<string, Set<string>>();
  for (const source of sources) {
    if (!source.citation_key) continue;
    const identity = `${source.grant_opportunity_document_id}:${source.document_id}`;
    const identities = documentsByKey.get(source.citation_key) ?? new Set<string>();
    identities.add(identity);
    documentsByKey.set(source.citation_key, identities);
  }

  const hrefs = new Map<string, string>();
  for (const source of sources) {
    if (!source.citation_key || hrefs.has(source.citation_key)) continue;
    if ((documentsByKey.get(source.citation_key)?.size ?? 0) > 1) continue;
    const link =
      links.find((item) => item.id === source.grant_opportunity_document_id) ??
      links.find((item) => item.document_id === source.document_id);
    if (link?.file_path) hrefs.set(source.citation_key, link.file_path);
  }
  return hrefs.size > 0 ? hrefs : undefined;
}

function OpportunityAssistantMessage({
  message,
  documentLinks,
}: {
  message: OpportunityDisplayMessage;
  documentLinks: GrantOpportunityDocumentLink[];
}) {
  // Reply polling replaces the chat payload wholesale, so every message's
  // citations array is a new reference on each tick. Key it by content so the
  // memoized markdown components (and their badge buttons) survive polls the
  // same way they survive the document links arriving.
  const citationsKey = JSON.stringify(message.citations ?? null);
  const citations = useMemo<CitationEntry[] | undefined>(
    () => (citationsKey === "null" ? undefined : JSON.parse(citationsKey)),
    [citationsKey],
  );
  const formattedContent = useMemo(
    () => normalizeCitationLinks(sanitizeLLMOutput(message.content), citations),
    [message.content, citations],
  );

  // Citations listens for this event (keyed by message id) and opens the
  // matching reference flyout; same contract as the staff BotMessage.
  const handleCitationClick = useCallback(
    (key: string) => {
      if (!key || typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("citation:open", { detail: { messageId: message.id, key } }),
      );
    },
    [message.id],
  );

  // buildMarkdownComponents returns a fresh `a` component; recreating it per
  // render would make React remount every badge button on unrelated pane
  // re-renders (e.g. the document links arriving), dropping in-flight clicks.
  // Built even with no citations: a cite marker the backend could not bind
  // (citations came back empty) must render as an inert "?" badge, never as
  // react-markdown's default <a href=""> that reloads the page on click.
  const markdownComponents = useMemo(() => {
    const citationLabels = (citations ?? []).map(citationEntryKey).filter(Boolean);
    const markerStyle = detectCitationMarkerStyle(formattedContent, citationLabels);
    const displayNumbers = buildCitationDisplayNumbers(citations, formattedContent);
    return buildMarkdownComponents(handleCitationClick, displayNumbers, markerStyle, citations);
  }, [citations, formattedContent, handleCitationClick]);

  const sourceHrefByKey = useMemo(
    () => sourceHrefsForMessage(message.citationSources, documentLinks),
    [message.citationSources, documentLinks],
  );

  return (
    <ChatAssistantBubble
      content={formattedContent}
      sanitize={false}
      createdAt={message.createdAt}
      compact
      markdownComponents={markdownComponents}
      urlTransform={citationUrlTransform}
      afterContent={
        citations?.length ? (
          <Citations
            message={{
              id: String(message.id),
              content: message.content,
              meta_data: { citation: citations },
            }}
            citationContent={formattedContent}
            sourceHrefByKey={sourceHrefByKey}
          />
        ) : null
      }
    />
  );
}

type OpportunityResearchPaneProps = {
  municipalityId: number;
  opportunityId: number;
  opportunityTitle: string;
};

export function OpportunityResearchPane({
  municipalityId,
  opportunityId,
  opportunityTitle,
}: OpportunityResearchPaneProps) {
  const [chat, setChat] = useState<ChatConversation | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryQuestion, setRetryQuestion] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  // A 404 *after* the pane has loaded means the opportunity was deleted or
  // access was revoked mid-session. Unlike the mount-time 404 it doesn't blank
  // the pane — the conversation stays readable — but the composer has to stop
  // accepting sends, or Send becomes exactly the dead end that Retry is
  // deliberately withheld to avoid.
  const [accessLost, setAccessLost] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Every opportunity switch and unmount bumps the generation; an in-flight
  // poll compares its captured generation on every wake-up and quietly exits
  // when it has been superseded. A boolean ref can't do this: the next mount's
  // reset would revive the stale poll. "New conversation" deliberately does
  // not bump it, and doesn't need to: it is gated on `loading`, so no poll can
  // be in flight when it runs.
  const pollGenerationRef = useRef(0);

  const awaitReply = useCallback(
    async (chatId: number, question: ChatMessage, generation: number) => {
      try {
        for (let attempt = 0; attempt < OPPORTUNITY_RESEARCH_POLL_MAX_ATTEMPTS; attempt++) {
          await new Promise((resolve) =>
            window.setTimeout(resolve, OPPORTUNITY_RESEARCH_POLL_INTERVAL_MS),
          );
          if (pollGenerationRef.current !== generation) return;
          let refreshed: ChatConversation;
          try {
            refreshed = await getOpportunityResearchChat(chatId);
          } catch (err) {
            if (pollGenerationRef.current !== generation) return;
            // A 404 is terminal by design; any other failure is a transient
            // blip while the server is still generating, so keep polling.
            // Aborting here would offer a Retry that re-posts a question
            // whose answer is about to land.
            if (err instanceof OpportunityResearchUnavailableError) throw err;
            continue;
          }
          if (pollGenerationRef.current !== generation) return;
          setChat(refreshed);
          if (hasBotReplyTo(refreshed, question)) return;
        }
        throw new Error("This is taking longer than expected.");
      } catch (err) {
        if (pollGenerationRef.current !== generation) return;
        setError(err instanceof Error ? err.message : "Could not retrieve the answer.");
        // The 404 means missing-or-forbidden and never heals, so a Retry
        // button would be a dead end.
        if (err instanceof OpportunityResearchUnavailableError) {
          setAccessLost(true);
        } else {
          setRetryQuestion(question.content);
        }
      } finally {
        if (pollGenerationRef.current === generation) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    pollGenerationRef.current += 1;
    const generation = pollGenerationRef.current;
    setChat(null);
    setQuery("");
    setLoading(false);
    setArchiving(false);
    setError(null);
    setRetryQuestion(null);
    setUnavailable(false);
    setAccessLost(false);
    setInitializing(true);
    findLatestOpportunityResearchChat(opportunityId)
      .then(async (latest) => {
        if (cancelled || !latest) return;
        const loaded = await getOpportunityResearchChat(latest.id);
        if (cancelled) return;
        setChat(loaded);
        // A newest from_human message means a reply was generating when this
        // pane last unmounted (tab switch, page refresh) — resume waiting for
        // it instead of presenting a silently dead conversation.
        const newest = loaded.messages?.[loaded.messages.length - 1];
        if (newest && newest.source === "from_human") {
          setLoading(true);
          void awaitReply(loaded.id, newest, generation);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof OpportunityResearchUnavailableError) {
          setUnavailable(true);
        } else {
          setError(err instanceof Error ? err.message : "Could not load the conversation.");
        }
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => {
      cancelled = true;
      pollGenerationRef.current += 1;
    };
  }, [opportunityId, awaitReply]);

  // Citation sources resolve to package document URLs against the live links.
  // Same query key as GrantOpportunityDetail / GrantPlanningModal, so package
  // uploads and unlinks invalidate this copy too (no fileViewerEnabled gate
  // here: citations need links even when the file viewer tab is off). A
  // failed load is silent: citations still render, just without open links.
  const { data: documentLinks = [] } = useQuery({
    queryKey: ["grant_opportunity_documents", municipalityId, opportunityId],
    queryFn: () => listGrantOpportunityDocuments(municipalityId, opportunityId),
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chat, loading]);

  const sendQuestion = async (content: string) => {
    const generation = pollGenerationRef.current;
    setLoading(true);
    setError(null);
    setRetryQuestion(null);

    try {
      const activeChat =
        chat ?? (await createOpportunityResearchChat(municipalityId, opportunityId));
      if (pollGenerationRef.current !== generation) return;
      if (!chat) setChat(activeChat);

      const userMessage = await postOpportunityResearchMessage(activeChat.id, content);
      if (pollGenerationRef.current !== generation) return;
      // Clears the composer only while it still holds the question that was
      // sent, so a Retry doesn't wipe a draft typed after the failure.
      setQuery((current) => (current.trim() === content ? "" : current));
      setChat((current) =>
        current && current.id === activeChat.id
          ? { ...current, messages: [...(current.messages ?? []), userMessage] }
          : current,
      );

      void awaitReply(activeChat.id, userMessage, generation);
    } catch (err) {
      if (pollGenerationRef.current !== generation) return;
      // Includes the 404 case: an inline alert keeps the conversation and the
      // typed question on screen instead of blanking the pane.
      setError(err instanceof Error ? err.message : "Could not send your message.");
      if (err instanceof OpportunityResearchUnavailableError) setAccessLost(true);
      else setRetryQuestion(content);
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    const trimmed = query.trim();
    if (!trimmed || loading || archiving || unavailable || accessLost) return;
    void sendQuestion(trimmed);
  };

  // Retry re-posts the question rather than resuming the poll: after a
  // timeout the worker may have given up without ever writing a reply, and
  // only a new message triggers a fresh generation. If the original answer
  // does land late, replies are matched by response_to_message_id, so it
  // can't be mistaken for the retry's answer.
  const handleRetry = () => {
    if (!retryQuestion || loading || archiving || unavailable || accessLost) return;
    void sendQuestion(retryQuestion);
  };

  const startNewConversation = async () => {
    if (loading || archiving || !chat) return;
    const previous = chat;
    const generation = pollGenerationRef.current;
    // The composer stays disabled until the archive settles. A send in that
    // window would create the replacement chat while a failed archive was
    // still on its way to restoring the old one, and the pane would end up
    // showing the archived thread over a live chat holding the question.
    setArchiving(true);
    setChat(null);
    setError(null);
    setRetryQuestion(null);
    try {
      await archiveOpportunityResearchChat(previous.id);
    } catch (err) {
      if (pollGenerationRef.current !== generation) return;
      if (err instanceof OpportunityResearchUnavailableError) {
        // Already-inaccessible means already gone from the index, so staying
        // cleared is correct. It also means the same thing every other 404
        // does: lock, rather than leaving an inviting composer that will only
        // discover the loss on the next send.
        setAccessLost(true);
        // This path cleared the alert on its way in, and it is the one lock the
        // placeholder can't be trusted to explain: a draft survives New
        // conversation, and a placeholder only shows on an empty field. Restate
        // the reason so every locked state carries a visible one.
        setError(err.message);
      } else {
        // Restore rather than silently resurrecting the thread on the next
        // mount.
        setChat((current) => current ?? previous);
        setError("Could not start a new conversation. Try again.");
      }
    } finally {
      setArchiving(false);
    }
  };

  // Stable per chat payload so OpportunityAssistantMessage's memoized markdown
  // components survive unrelated pane re-renders.
  const messages = useMemo(() => visibleMessages(chat), [chat]);

  // Backs up the alert rather than replacing it. Every path that sets
  // `accessLost` also sets `error`, so the reason is always on screen; this
  // just puts it where the dead control is, for the empty-composer case.
  const composerPlaceholder = accessLost
    ? "Research Assistant unavailable for this opportunity"
    : "Ask about this opportunity…";

  const composerAlerts = error ? (
    <ChatInlineAlert variant="destructive">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0">{error}</span>
        {retryQuestion ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 shrink-0 px-2 text-xs"
            onClick={handleRetry}
            disabled={loading || archiving}
          >
            <RotateCcw className="mr-1.5 h-3 w-3" aria-hidden />
            Retry
          </Button>
        ) : null}
      </div>
    </ChatInlineAlert>
  ) : null;

  if (unavailable) {
    return (
      <div
        className="flex h-full min-h-0 w-full flex-col items-center justify-center gap-2 rounded-lg border bg-background p-6 text-center"
        data-testid="opportunity-research-unavailable"
      >
        <OrdinalIcon className="h-8 w-8" fill="hsl(var(--primary) / 0.08)" />
        <p className="text-sm text-muted-foreground">
          The Research Assistant isn&apos;t available for this opportunity.
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col rounded-lg border bg-background"
      data-testid="opportunity-research-pane"
    >
      <div className="flex h-11 shrink-0 items-center gap-2 border-b px-3">
        <OrdinalIcon className="h-6 w-6 shrink-0" fill="hsl(var(--primary) / 0.08)" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold">Opportunity Research Assistant</h3>
          <p className="truncate text-xs text-muted-foreground">{opportunityTitle}</p>
        </div>
        {messages.length > 0 && !accessLost ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 px-2 text-muted-foreground"
            onClick={startNewConversation}
            disabled={loading || archiving}
          >
            <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            New conversation
          </Button>
        ) : null}
      </div>

      <ChatMessageThread compact messageCount={messages.length} scrollAnchorRef={messagesEndRef}>
        {messages.length === 0 && !initializing ? (
          <ChatWelcomeBubble message={OPPORTUNITY_RESEARCH_WELCOME} compact />
        ) : null}
        {messages.map((message) =>
          message.role === "user" ? (
            <ChatUserBubble
              key={message.id}
              content={message.content}
              createdAt={message.createdAt}
              compact
            />
          ) : (
            <OpportunityAssistantMessage
              key={message.id}
              message={message}
              documentLinks={documentLinks}
            />
          ),
        )}
        {loading ? <ChatLoadingIndicator message="Working on your question…" compact /> : null}
      </ChatMessageThread>

      <div className="shrink-0 border-t p-4">
        <ChatComposer
          value={query}
          onChange={setQuery}
          onSubmit={handleSubmit}
          placeholder={composerPlaceholder}
          disabled={initializing || archiving || accessLost}
          loading={loading}
          loadingLabel="Asking…"
          ariaLabel="Question about this opportunity"
          compact
          alerts={composerAlerts}
        />
      </div>
    </div>
  );
}
